import { zodResolver } from '@hookform/resolvers/zod';
import { Fade } from '@mui/material';
import type { NewSubsidiary, Subsidiary, SubsidiaryInput } from '@vy/types';
import { NewSubsidiarySchema } from '@vy/types';
import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { VyDialog } from '../../../../../../../components/new-design/feedback/VyDialog';
import { useUpdateSubsidiary } from '../../../../../../../hooks/requests/use-update-subsidiary';
import { queryKeys } from '../../../../../../../services/query-key/query-keys';
import type { useDialog } from '../../../../../../../hooks/use-dialog';
import { useVyStepper } from '../../../../../../../hooks/use-vy-stepper';
import { runMutation } from '../../../../../../../utils/vy-mutation';
import type { CreateSubsidiaryDialogStep } from '../../entities-tab-content-types/create-subsidiary-dialog-step';
import { CREATE_SUBSIDIARY_DIALOG_STEPS } from '../../entities-tab-content-types/create-subsidiary-dialog-step';
import { AccountInfoTabContent } from './create-subsidiary-dialog-components/AccountInfoTabContent';
import { BankInfoTabContent } from './create-subsidiary-dialog-components/BankInfoTabContent';

const CreateSubsidiaryDialogSteps: Record<CreateSubsidiaryDialogStep, (...args: any[]) => React.ReactElement> =
  {
    [CREATE_SUBSIDIARY_DIALOG_STEPS.accountInfo]: (primarySelectable: boolean) => (
      <AccountInfoTabContent primarySelectable={primarySelectable} />
    ),
    [CREATE_SUBSIDIARY_DIALOG_STEPS.bankInfo]: () => <BankInfoTabContent />,
  };

type UpdateSubsidiaryDialogProps = ReturnType<typeof useDialog> & {
  allSubsidiaries: Subsidiary[];
  subsidiary: Subsidiary;
};

export const UpdateSubsidiaryDialog: React.FC<UpdateSubsidiaryDialogProps> = ({
  isDialogOpen,
  handleClose,
  subsidiary,
  allSubsidiaries,
}) => {
  const { currentStep, nextStep, previousStep, isFirstStep, isFinalStep, goToStep } = useVyStepper(
    CREATE_SUBSIDIARY_DIALOG_STEPS.accountInfo,
    CREATE_SUBSIDIARY_DIALOG_STEPS,
  );

  const queryClient = useQueryClient();
  const updateSubsidiaryMutation = useUpdateSubsidiary(subsidiary.id);

  const isAnySubsidiaryPrimary = allSubsidiaries.some(({ isPrimary }) => isPrimary);

  const methods = useForm<SubsidiaryInput, unknown, NewSubsidiary>({
    resolver: zodResolver(NewSubsidiarySchema),
  });

  const handleCloseDialog = () => {
    handleClose();
    goToStep(CREATE_SUBSIDIARY_DIALOG_STEPS.accountInfo);
  };

  const handleSubmit = methods.handleSubmit(async (newSubsidiary) => {
    await runMutation(
      updateSubsidiaryMutation,
      {
        subsidiary: newSubsidiary,
      },
      {
        pending: 'Updating subsidiary...',
        error: 'Failed to update subsidiary',
        success: 'Subsidiary updated successfully',
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries(queryKeys.subsidiaries());
          handleCloseDialog();
          methods.reset();
        },
      },
    );
  });

  useEffect(() => {
    methods.reset(subsidiary);
  }, [methods, subsidiary]);

  return (
    <VyDialog
      onSubmit={handleSubmit}
      open={isDialogOpen}
      onClose={handleCloseDialog}
      label="Subsidiary"
      ctaText={isFinalStep ? 'Update' : 'Next'}
      cancelButtonText={!isFirstStep ? 'Back' : undefined}
      cancelButtonProps={{
        onClick: () => {
          previousStep();
        },
      }}
      ctaButtonProps={{
        type: isFinalStep ? 'submit' : 'button',
        onClick: !isFinalStep
          ? async () => {
              nextStep();
            }
          : handleSubmit,
      }}
      ctaDisabled={methods.formState.isSubmitting}
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit}>
          {Object.entries(CreateSubsidiaryDialogSteps).map(([dialogStep, render]) => (
            <Fade
              key={dialogStep}
              hidden={currentStep.toString() !== dialogStep}
              in={currentStep.toString() === dialogStep}
            >
              <div className="h-full w-full">
                {currentStep.toString() === dialogStep &&
                  render(subsidiary.isPrimary || !isAnySubsidiaryPrimary)}
              </div>
            </Fade>
          ))}
        </form>
      </FormProvider>
    </VyDialog>
  );
};
